import { Component, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { RebalancingModel } from '@core/models/rebalancing-model.model';
import { RebalancesService } from '@core/services/rebalance.service';
import { FormControlFields } from '@core/utils/object-helpers';
import { AlertComponent, ButtonComponent, IconComponent } from '@shared/components';
import { EIcons } from '@shared/models';
import { MaterialModule } from '@shared/modules';
import { Subscription } from 'rxjs';
import { ThirdStepForm } from '../interface/forms.interface';

@Component({
  selector: 'app-third-step',
  standalone: true,
  imports: [ReactiveFormsModule, MaterialModule, ButtonComponent, IconComponent],
  templateUrl: './third-step.component.html',
  styleUrl: './third-step.component.scss'
})
export class ThirdStepComponent implements OnInit, OnDestroy {
  private readonly rebalancingsServices = inject(RebalancesService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  readonly EIcons = EIcons;
  readonly #subscriptions = new Subscription();

  form: FormGroup<FormControlFields<ThirdStepForm>>;
  file = signal<File | null>(null);
  previewImage = signal<string | ArrayBuffer | null>('');
  isDisabled: boolean = false;

  @Input() rebalance: RebalancingModel | null = null;
  @Input() isInternal: boolean = true;

  constructor() {
    this.form = new FormGroup<FormControlFields<ThirdStepForm>>({
      plate: new FormControl(''),
      vin: new FormControl(''),
      acountable: new FormControl('', { validators: [Validators.required, Validators.pattern('^[A-Za-zÀ-ÖØ-öø-ÿ\\s]+$')] }),
      rebalancing_ok: new FormControl(true),
      obs: new FormControl(''),
    });
  }

  ngOnInit(): void {
    this.#setForm()
  }

  #setForm() {
    this.form.controls.plate?.enable();
    this.form.controls.vin?.enable();

    this.form.controls.plate?.setValue(this.rebalance!.unit.plate);
    this.form.controls.vin?.setValue(this.rebalance!.unit.vin);

    this.form.controls.plate?.disable();
    this.form.controls.vin?.disable();
  }

  ngOnDestroy(): void {
    this.#subscriptions.unsubscribe();
  }

  thirdFormHasError(control: string, error: string): boolean | undefined {
    return this.form.get(control)?.touched && this.form.get(control)?.hasError(error);
  }

  parseFileToURL(file: File | any) {
    if (file.url) {
      this.previewImage.set(file.url);
      return;
    }

    return new Promise((resolve, reject) => {
      const lector = new FileReader();

      lector.onload = (event) => {
        this.previewImage.set(event.target!.result);
        resolve(event.target!.result);
      };

      lector.onerror = (error) => {
        console.log(error);
        reject(new Error('Error al leer el archivo.'));
      };

      lector.readAsDataURL(file);
    });
  }

  async setFiles(file: any, selector: any) {
    file.preventDefault();

    if (file.target.files[0]) {
      this.file.set(file.target.files[0]);
    }

    await this.parseFileToURL(this.file());

    selector.value = '';
  }

  save() {
    const thirdForm = this.form.value;

    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    delete thirdForm.obs, thirdForm.acountable;

    const data = {
      data: thirdForm,
      step: 3,
      responsible: this.form.controls.acountable.value,
      comments: this.form.controls.obs?.value
    };

    this.form.disable();

    const extensionIndex = this.file()?.name.lastIndexOf('.');
    const extension = this.file()?.name.slice(extensionIndex);
    const fileName = `reb_${this.rebalance!.id}_${data.step + 1}_${new Date().getTime()}${extension}`;
    const newFile = new File([this.file()!], fileName, {
      type: this.file()?.type,
      lastModified: this.file()?.lastModified,
    });
    this.isDisabled = true;

    this.rebalancingsServices.sendLastRebalancingData(JSON.stringify(data), this.rebalance!.id, newFile).subscribe({
      next: () => {
        this.dialog.open(AlertComponent, {
          data: {
            type: 'success',
            message: 'Rebalanceo guardado',
            successButtonText: 'Aceptar',
          },
        }).afterClosed().subscribe(() => {
          if (this.isInternal) {
            this.router.navigate(['/page/mex/services-and-repairs/rebalancings'])
          }
        });
      },
      error: err => {
        this.isDisabled = false;
        console.log(err.error.error);

      },
    })
  }

}
